// S-03 / E-09: compare our shader conversion (planes → R'G'B') with the browser's own conversion of a VideoFrame.

import { ALL_FORMATS, BlobSource, EncodedPacketSink, Input } from 'mediabunny';
import { SourceClient } from '../media/source-client';
import { GLRenderer } from '../render/gl-renderer';
import { resolveColor } from '../core/color';
import type { DecodedFrame } from '../media/protocol';

export interface ColorPoint {
  name: string;
  x: number;
  y: number;
}

export interface ColorCheckResult {
  format: string | null;
  bitDepth: number;
  ours: Record<string, [number, number, number]>;
  browser: Record<string, [number, number, number]>;
  /** Raw Y/Cb/Cr codes read from the planes at each point (C-05). */
  codes: Record<string, [number, number, number]>;
}

async function firstVideoFrame(file: File): Promise<VideoFrame> {
  const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
  const track = (await input.getPrimaryVideoTrack())!;
  const config = (await track.getDecoderConfig())!;
  const sink = new EncodedPacketSink(track);
  return new Promise<VideoFrame>((resolve, reject) => {
    let got = false;
    const dec = new VideoDecoder({
      output: (f) => {
        if (got) return f.close();
        got = true;
        resolve(f);
      },
      error: (e) => reject(e),
    });
    dec.configure(config);
    void (async () => {
      let n = 0;
      for await (const p of sink.packets()) {
        dec.decode(p.toEncodedVideoChunk());
        if (++n > 4) break;
      }
      await dec.flush();
    })();
  });
}

function codesAt(f: DecodedFrame, x: number, y: number): [number, number, number] {
  const l = f.layout;
  if (f.format === 'RGBA') return [NaN, NaN, NaN];
  const high = f.format.includes('P1');
  const u8 = new Uint8Array(f.data);
  const u16 = high ? new Uint16Array(f.data) : null;
  const get = (p: number, xx: number, yy: number, comp = 0, comps = 1) =>
    u16 ? u16[(l[p].offset + yy * l[p].stride) / 2 + xx * comps + comp] : u8[l[p].offset + yy * l[p].stride + xx * comps + comp];
  const Y = get(0, x, y);
  if (f.format === 'NV12') return [Y, get(1, x >> 1, y >> 1, 0, 2), get(1, x >> 1, y >> 1, 1, 2)];
  const sx = f.format.startsWith('I444') ? 0 : 1;
  const sy = f.format.startsWith('I420') ? 1 : 0;
  return [Y, get(1, x >> sx, y >> sy), get(2, x >> sx, y >> sy)];
}

export async function colorCheck(file: File, points: ColorPoint[]): Promise<ColorCheckResult> {
  const c = new SourceClient(file);
  const info = await c.open();
  const frame = await new Promise<DecodedFrame>((resolve, reject) => {
    const off = c.addFrameListener((i) => {
      if (i !== 0) return;
      off();
      const f = c.getFrame(0);
      if (f) resolve(f);
      else reject(new Error(c.frameErrors.get(0) ?? 'no frame'));
    });
    c.request([0], [0]);
  });
  const canvas = new OffscreenCanvas(16, 16);
  const r = new GLRenderer(canvas);
  r.uploadFrame('S', frame, resolveColor(info.spec), 'k');
  const ours: ColorCheckResult['ours'] = {};
  const codes: ColorCheckResult['codes'] = {};
  for (const p of points) {
    const v = r.readSourceRGB('S', p.x, p.y, 1, 1)!;
    ours[p.name] = [0, 1, 2].map((i) => Math.round(Math.min(1, Math.max(0, v[i])) * 255)) as [number, number, number];
    codes[p.name] = codesAt(frame, p.x, p.y);
  }
  // Browser conversion: VideoFrame uploaded as an RGBA8 texture and read back.
  const vf = await firstVideoFrame(file);
  const gl = r.gl;
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, vf);
  const fb = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  const browser: ColorCheckResult['browser'] = {};
  const px = new Uint8Array(4);
  for (const p of points) {
    gl.readPixels(p.x, p.y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
    browser[p.name] = [px[0], px[1], px[2]];
  }
  const fmt = frame.sourceFormat;
  vf.close();
  r.dispose();
  c.dispose();
  return { format: fmt, bitDepth: info.spec.bitDepth, ours, browser, codes };
}
